import { useState, useEffect } from "react";
import { useAppContext } from "../Context";

//import components
import * as Options from "../utils/PropertyOptions";
import Addonfilter from "../dynamic/Addonfilter";

//import tags
import { Col } from "react-bootstrap";
import Select from "react-select";
import { toast } from "react-toastify";

//import resources
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMagnifyingGlass,
  faSave,
  faSearchPlus,
  faSliders,
  faLocationDot,
  faRulerCombined,
  faMoneyBillWave,
  faHashtag,
} from "@fortawesome/free-solid-svg-icons";

//import translation
import { useTranslation } from "react-i18next";
import "../../i18n";

// import Api
import { save_filter } from "../../api/Api";

export default function Filter({ setQueryData, queryData, locale }) {
  //get locale
  const { t } = useTranslation();

  const [searchQueryData, setSearchQueryData] = useState({});
  const [filterModal, setFilterModal] = useState(false);
  const [saveFilter, setSaveFilter] = useState({});

  const PRICE_MIN = 0;
  const PRICE_MAX = 10000000;
  const PRICE_STEP = 5000;

  const [currency, setCurrency] = useState(
    searchQueryData?.currency ? searchQueryData.currency : "GEL",
  );

  const { isLoggedIn } = useAppContext();

  const queryParams = new URLSearchParams(window.location.search);

  useEffect(() => {
    const newSearchQueryData = {};

    queryParams.forEach((value, key) => {
      newSearchQueryData[key] = value.includes(",") ? value.split(",") : value;
    });

    setSearchQueryData((prevSearchQueryData) => ({
      ...prevSearchQueryData,
      ...newSearchQueryData,
    }));

    if (newSearchQueryData.currency) {
      setCurrency(newSearchQueryData.currency);
    }
  }, []);

  useEffect(() => {
    setSearchQueryData((prevsearchQueryData) => ({
      ...prevsearchQueryData,
      currency,
    }));
  }, [currency]);

  // hide scroll when modal is opened
  useEffect(() => {
    if (filterModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [filterModal]);

  function handleChangeSelect(selectedOption, actionMeta) {
    if (actionMeta && selectedOption) {
      let values;

      if (Array.isArray(selectedOption)) {
        values = selectedOption.map((option) => option.value);
      } else {
        values = selectedOption.value;
      }

      setSearchQueryData((prevsearchQueryData) => ({
        ...prevsearchQueryData,
        [actionMeta.name]: values,
      }));
    }
  }

  const filter = () => {
    setQueryData((prevQueryData) => {
      // Get all keys from the searchQueryData
      const updatedQueryData = { ...searchQueryData, currency };
      const cleanedPrevQueryData = { ...prevQueryData };

      // Remove any keys from prevQueryData that aren't in updatedQueryData
      Object.keys(cleanedPrevQueryData).forEach((key) => {
        if (!(key in updatedQueryData)) {
          delete cleanedPrevQueryData[key];
        }
      });

      // Merge the updated query data with prevQueryData (after cleanup)
      return {
        ...cleanedPrevQueryData,
        ...updatedQueryData,
        page: 1, // Reset the page to 1
      };
    });
  };

  const filterSave = async () => {
    if (!isLoggedIn) {
      toast.error(t("please_auth"), {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
    }
    const header = {
      Authorization: "Bearer " + localStorage.getItem("token"),
    };

    try {
      await save_filter(setSaveFilter, queryData, header);
      if (saveFilter?.status == "ok") {
        toast.success(t("search_success"), {
          position: "bottom-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
        });
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleChangeInpputText = (e) => {
    setSearchQueryData((prevsearchQueryData) => ({
      ...prevsearchQueryData,
      key: e.target.value,
    }));
  };

  const handleChangeNumberInput = (e) => {
    const { name, value } = e.target;

    setSearchQueryData((prevsearchQueryData) => ({
      ...prevsearchQueryData,
      [name]: value,
    }));
  };

  const parsePriceValue = (value, fallback) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const boundedFrom = Math.min(
    Math.max(
      parsePriceValue(searchQueryData?.price_from, PRICE_MIN),
      PRICE_MIN,
    ),
    PRICE_MAX,
  );
  const boundedTo = Math.min(
    Math.max(parsePriceValue(searchQueryData?.price_to, PRICE_MAX), PRICE_MIN),
    PRICE_MAX,
  );
  const safePriceFrom = Math.min(boundedFrom, boundedTo);
  const safePriceTo = Math.max(boundedFrom, boundedTo);
  const priceFromPercent =
    ((safePriceFrom - PRICE_MIN) / (PRICE_MAX - PRICE_MIN)) * 100;
  const priceToPercent =
    ((safePriceTo - PRICE_MIN) / (PRICE_MAX - PRICE_MIN)) * 100;

  const handlePriceRangeFrom = (e) => {
    const nextFrom = Number(e.target.value);
    setSearchQueryData((prevsearchQueryData) => {
      const currentTo = parsePriceValue(
        prevsearchQueryData?.price_to,
        PRICE_MAX,
      );
      return {
        ...prevsearchQueryData,
        price_from: Math.min(nextFrom, currentTo),
      };
    });
  };

  const handlePriceRangeTo = (e) => {
    const nextTo = Number(e.target.value);
    setSearchQueryData((prevsearchQueryData) => {
      const currentFrom = parsePriceValue(
        prevsearchQueryData?.price_from,
        PRICE_MIN,
      );
      return {
        ...prevsearchQueryData,
        price_to: Math.max(nextTo, currentFrom),
      };
    });
  };

  const formatSelectOptionLabel = (option, { context, selectValue }) => {
    if (context !== "menu") {
      return option.label;
    }

    const isSelected =
      Array.isArray(selectValue) &&
      selectValue.some((selected) => selected.value === option.value);

    return (
      <div className="rfOptionLabel">
        <span className="rfOptionText">{option.label}</span>
        <span className={`rfOptionCheck ${isSelected ? "active" : ""}`}>✓</span>
      </div>
    );
  };

  return (
    <>
      <Col xxl={12}>
        <div className="rfStickyBar">
          <div className="rfFilterCard">
            <div className="rfRow">
              
              <div className="rfField">
                <label className="rfLabel">{t("choose_property_type")}</label>
                <Select
                  id="property-type"
                  value={Options.propertyTypeOptions.filter((opt) =>
                    (searchQueryData?.property_type || "").includes(opt.value),
                  )}
                  name="property_type"
                  onChange={handleChangeSelect}
                  options={Options.propertyTypeOptions}
                  placeholder={t("choose_property_type")}
                  className="rfReactSelect"
                  classNamePrefix="rf-select"
                  isMulti
                  hideSelectedOptions={false}
                  closeMenuOnSelect={false}
                  formatOptionLabel={formatSelectOptionLabel}
                />
              </div>

              <div className="rfField">
                <label className="rfLabel">
                  {t("choose_transaction_type")}
                </label>
                <Select
                  id="transaction-type"
                  value={Options.transactionTypeOptions.filter((opt) =>
                    (searchQueryData?.transaction_type || "").includes(
                      opt.value,
                    ),
                  )}
                  name="transaction_type"
                  onChange={handleChangeSelect}
                  options={Options.transactionTypeOptions}
                  placeholder={t("choose_transaction_type")}
                  className="rfReactSelect"
                  classNamePrefix="rf-select"
                  isMulti
                  hideSelectedOptions={false}
                  closeMenuOnSelect={false}
                  formatOptionLabel={formatSelectOptionLabel}
                />
              </div>

              <div className="rfField">
                <label className="rfLabel">{t("city")}</label>
                <div className="rfInputWithIcon">
                  <FontAwesomeIcon
                    icon={faLocationDot}
                    className="rfInputIcon"
                  />
                  <Select
                    id="city"
                    value={Options.cityOptions.filter((opt) =>
                      (searchQueryData?.city || "").includes(opt.value),
                    )}
                    name="city"
                    onChange={handleChangeSelect}
                    options={Options.cityOptions}
                    placeholder={t("city")}
                    className="rfReactSelect"
                    classNamePrefix="rf-select"
                    isMulti
                    hideSelectedOptions={false}
                    closeMenuOnSelect={false}
                    formatOptionLabel={formatSelectOptionLabel}
                  />
                </div>
              </div>
            </div>

            <div className="rfRow rfRowSeparated">
              <div className="rfField">
                <label className="rfLabel">{t("area")}</label>
                <div className="rfPairGroup">
                  <div className="rfInputWithIcon">
                    <FontAwesomeIcon
                      icon={faRulerCombined}
                      className="rfInputIcon"
                    />
                    <input
                      type="number"
                      name="area_from"
                      value={searchQueryData?.area_from || ""}
                      onChange={handleChangeNumberInput}
                      placeholder={t("from")}
                    />
                  </div>
                  <div className="rfInputWithIcon">
                    <FontAwesomeIcon
                      icon={faRulerCombined}
                      className="rfInputIcon"
                    />
                    <input
                      type="number"
                      name="area_to"
                      value={searchQueryData?.area_to || ""}
                      onChange={handleChangeNumberInput}
                      placeholder={t("to")}
                    />
                  </div>
                </div>
              </div>

              <div className="rfField">
                <label className="rfLabel">{t("price")}</label>
                <div className="rfPriceBlock">
                  <div className="rfDualRangePreview">
                    <span className="rfDualRangeTrack"></span>
                    <span
                      className="rfDualRangeActive"
                      style={{
                        left: `${priceFromPercent}%`,
                        right: `${100 - priceToPercent}%`,
                      }}
                    ></span>
                    <input
                      type="range"
                      className="rfRangeInput"
                      min={PRICE_MIN}
                      max={PRICE_MAX}
                      step={PRICE_STEP}
                      value={safePriceFrom}
                      onChange={handlePriceRangeFrom}
                    />
                    <input
                      type="range"
                      className="rfRangeInput"
                      min={PRICE_MIN}
                      max={PRICE_MAX}
                      step={PRICE_STEP}
                      value={safePriceTo}
                      onChange={handlePriceRangeTo}
                    />
                  </div>
                  <div className="rfPairGroup">
                    <div className="rfInputWithIcon rfInputWithCurrency">
                      <FontAwesomeIcon
                        icon={faMoneyBillWave}
                        className="rfInputIcon"
                      />
                      <input
                        type="number"
                        name="price_from"
                        value={searchQueryData?.price_from || ""}
                        onChange={handleChangeNumberInput}
                        placeholder={t("from")}
                      />
                      <span className="rfCurrencyBadge">{currency}</span>
                    </div>
                    <div className="rfInputWithIcon rfInputWithCurrency">
                      <FontAwesomeIcon
                        icon={faMoneyBillWave}
                        className="rfInputIcon"
                      />
                      <input
                        type="number"
                        name="price_to"
                        value={searchQueryData?.price_to || ""}
                        onChange={handleChangeNumberInput}
                        placeholder={t("to")}
                      />
                      <span className="rfCurrencyBadge">{currency}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rfField">
                <label className="rfLabel">{t("currency")}</label>
                <div
                  className="rfCurrencySwitch"
                  role="group"
                  aria-label={t("currency")}
                >
                  {["GEL", "USD", "EUR"].map((cur) => (
                    <button
                      type="button"
                      key={cur}
                      className={`rfCurrencyBtn ${currency === cur ? "active" : ""}`}
                      onClick={() => setCurrency(cur)}
                    >
                      {cur}
                    </button>
                  ))}
                </div>
              </div>

              <div className="rfField">
                <label className="rfLabel">{t("search")}</label>
                <div className="rfInputWithIcon">
                  <FontAwesomeIcon icon={faHashtag} className="rfInputIcon" />
                  <input
                    type="text"
                    name="key"
                    value={searchQueryData?.key || ""}
                    onChange={handleChangeInpputText}
                    placeholder="ID, საკადასტრო კოდი, სათაური..."
                  />
                </div>
              </div>

              <div className="rfField">
                <label className="rfLabel">{t("condition")}</label>
                <Select
                  id="condition"
                  value={Options.propertyConditionOptions.filter((opt) =>
                    (searchQueryData?.condition || "").includes(opt.value),
                  )}
                  name="condition"
                  onChange={handleChangeSelect}
                  options={Options.propertyConditionOptions}
                  placeholder={t("choose_condition")}
                  className="rfReactSelect"
                  classNamePrefix="rf-select"
                  isMulti
                  hideSelectedOptions={false}
                  closeMenuOnSelect={false}
                  formatOptionLabel={formatSelectOptionLabel}
                />
              </div>

              <div className="rfField">
                <label className="rfLabel">{t("project")}</label>
                <Select
                  id="project"
                  value={Options.propertyProjectOptions.filter((opt) =>
                    (searchQueryData?.project || "").includes(opt.value),
                  )}
                  name="project"
                  onChange={handleChangeSelect}
                  options={Options.propertyProjectOptions}
                  placeholder={t("choose_project")}
                  className="rfReactSelect"
                  classNamePrefix="rf-select"
                  isMulti
                  hideSelectedOptions={false}
                  closeMenuOnSelect={false}
                  formatOptionLabel={formatSelectOptionLabel}
                />
              </div>
            </div>

            <div className="rfActionRow">
              <div className="rfActions">
                <div className="rfActionsIns">
                  <button
                    type="button"
                    className="rfBtnGhost"
                    onClick={() => setFilterModal(true)}
                  >
                    <FontAwesomeIcon icon={faSearchPlus} />
                    <span>{t("detailed_filter")}</span>
                  </button>
                  <button
                    type="button"
                    className="rfIconBtn"
                    onClick={() => filterSave(true)}
                  >
                    <FontAwesomeIcon icon={faSave} />
                  </button>
                  <button
                    type="button"
                    onClick={() => filter()}
                    className="rfBtnPrimary"
                  >
                    {t("search")}
                    <FontAwesomeIcon icon={faMagnifyingGlass} />
                  </button>
                </div>
              </div>
            </div>

            <div className={`filterModal ` + (filterModal ? "active" : "")}>
              <div className="filterModalOuter">
                {filterModal ? (
                  <>
                    <Addonfilter
                      setFilterModal={setFilterModal}
                      searchQueryData={searchQueryData}
                      setSearchQueryData={setSearchQueryData}
                      handleChangeSelect={handleChangeSelect}
                      filter={filter}
                      filterSave={filterSave}
                    />
                  </>
                ) : (
                  ""
                )}
              </div>
            </div>
          </div>
        </div>
      </Col>
    </>
  );
}
